/**
 * Decides when a deployed service worker may reload this tab.
 *
 * vite-plugin-pwa's virtual:pwa-register helper (prompt mode) adds a
 * `controlling` listener that reloads the page as soon as the new worker
 * takes control. Workbox fires that from the browser's `controllerchange`
 * event, in every tab that has seen a waiting worker, including one with the
 * New ticket modal open. That listener is not configurable in the installed
 * plugin, so this module registers the worker itself and is the only reload path.
 *
 * A tab that is busy defers. An idle tab reloads. BroadcastChannel carries
 * two facts between tabs: "this tab has unsaved work" and "a tab has activated
 * the update". A busy signal for this tab blocks its reload. Where
 * BroadcastChannel is missing, the local busy check still defers this tab.
 */

export const PWA_UPDATE_CHANNEL = "iquest:pwa:auto-update";

// registration.update() only fetches the service worker script.
export const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

// How often to re-test the busy gate while an update is held.
export const DEFERRED_RETRY_MS = 30 * 1000;

// If the waiting worker never reaches `activated`, reload anyway so a tab
// does not sit on a stale shell after it has already asked the worker to take
// over. The gate runs again at that moment.
export const RELOAD_FALLBACK_MS = 5 * 1000;

// A half-published deploy can leave a tab reloading in a loop. Never reload
// twice inside the guard, and give up after MAX_RELOADS inside the window.
export const RELOAD_GUARD_MS = 60 * 1000;
export const RELOAD_WINDOW_MS = 5 * 60 * 1000;
export const MAX_RELOADS = 3;
export const RELOAD_STORAGE_KEY = "iquest:pwa:auto-reload";
export const TAB_STORAGE_KEY = "iquest:pwa:tab-id";

// A tab that crashes mid-edit must not block its own later reload forever.
export const BUSY_SIGNAL_TTL_MS = 45 * 1000;

/**
 * Surfaces that are only in the DOM while the user is part-way through
 * something: an open modal (new ticket, feedback, ticket detail, approval),
 * an open admin drawer, or an admin section holding unsaved edits.
 */
export const BUSY_SELECTOR = ".modal-backdrop, .ad-drawer-wrap.open, .ad-sec.dirty";

export type PwaUpdateMessage =
  | { type: "busy"; tabId: string }
  | { type: "idle"; tabId: string }
  | { type: "activated"; tabId: string };

export interface PwaUpdateSessionOptions {
  isBusy: () => boolean;
  reload: () => void;
  /** Ask the waiting worker to call skipWaiting(). Then call onWorkerActivated. */
  skipWaiting: () => void;
  /**
   * Another tab has activated the update. The host should look at the
   * registration and call onUpdateAvailable or onWorkerActivated. Doing that
   * here would reload a page that is already on the new worker.
   */
  onExternalActivation?: () => void;
  tabId?: string;
  now?: () => number;
  storage?: Storage;
  openChannel?: () => BroadcastChannel | null;
}

type Phase = "idle" | "activating" | "ready";

interface ReloadRecord {
  at: number;
  count: number;
}

/** The only call site that reloads the page. */
export function reloadPage(): void {
  window.location.reload();
}

export function openUpdateChannel(): BroadcastChannel | null {
  const Ctor = globalThis.BroadcastChannel;
  if (typeof Ctor !== "function") return null;
  try {
    return new Ctor(PWA_UPDATE_CHANNEL);
  } catch {
    return null;
  }
}

export function pageWouldInterrupt(typedSinceNavigation: boolean): boolean {
  return (
    document.querySelector(BUSY_SELECTOR) !== null ||
    typedSinceNavigation ||
    hasFilledTextarea()
  );
}

function hasFilledTextarea(): boolean {
  for (const field of document.querySelectorAll("textarea")) {
    if (field.disabled || field.readOnly) continue;
    if (field.value.trim() !== "") return true;
  }
  return false;
}

export function readReloadRecord(storage: Storage, now: number): ReloadRecord {
  try {
    const raw = storage.getItem(RELOAD_STORAGE_KEY);
    if (!raw) return { at: 0, count: 0 };
    const parsed = JSON.parse(raw) as Partial<ReloadRecord>;
    const at = Number(parsed.at);
    const count = Number(parsed.count);
    if (!Number.isFinite(at) || !Number.isFinite(count)) return { at: 0, count: 0 };
    return now - at > RELOAD_WINDOW_MS ? { at: 0, count: 0 } : { at, count };
  } catch {
    return { at: 0, count: 0 };
  }
}

function writeReloadRecord(storage: Storage, previous: ReloadRecord, now: number): void {
  try {
    storage.setItem(
      RELOAD_STORAGE_KEY,
      JSON.stringify({ at: now, count: previous.count + 1 }),
    );
  } catch {
    /* storage disabled; the reload still happens, just unguarded */
  }
}

/** Same idea as AdminFormEdit's helper. `"in"` is not enough: a missing
 * randomUUID can still be an own property whose value is undefined. */
function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `t_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

function createTabId(storage: Storage): string {
  try {
    const existing = storage.getItem(TAB_STORAGE_KEY);
    if (existing) return existing;
    const id = cryptoRandomId();
    storage.setItem(TAB_STORAGE_KEY, id);
    return id;
  } catch {
    return cryptoRandomId();
  }
}

function parseMessage(data: unknown): PwaUpdateMessage | null {
  if (!data || typeof data !== "object") return null;
  const msg = data as Partial<PwaUpdateMessage>;
  if (msg.type !== "busy" && msg.type !== "idle" && msg.type !== "activated") return null;
  if (typeof msg.tabId !== "string" || msg.tabId.length === 0 || msg.tabId.length > 80) return null;
  return { type: msg.type, tabId: msg.tabId };
}

/**
 * One browser tab's view of a waiting service worker.
 * `reload` runs at most once per page, and only after the busy gate passes.
 */
export class PwaUpdateSession {
  readonly tabId: string;
  private readonly now: () => number;
  private readonly storage: Storage;
  private readonly openChannel: () => BroadcastChannel | null;
  private channel: BroadcastChannel | null = null;
  private pending = false;
  private phase: Phase = "idle";
  private reloaded = false;
  private gaveUp = false;
  private disposed = false;
  /** This tab called skipWaiting. Only that tab announces activation. */
  private startedActivation = false;
  /** Activation was started by another tab, so this tab must not re-announce it. */
  private fromPeer = false;
  private lastPublishedBusy: boolean | null = null;
  /** tabId -> timestamp. A signal for THIS tab blocks its reload. */
  private busySignals = new Map<string, number>();

  constructor(private readonly opts: PwaUpdateSessionOptions) {
    this.now = opts.now ?? Date.now;
    this.storage = opts.storage ?? sessionStorage;
    this.openChannel = opts.openChannel ?? openUpdateChannel;
    this.tabId = opts.tabId ?? createTabId(this.storage);
  }

  start(): void {
    if (this.disposed) return;
    try {
      this.channel = this.openChannel();
      this.channel?.addEventListener("message", this.onChannelMessage);
    } catch {
      this.channel = null;
    }
    this.publishPresence();
  }

  stop(): void {
    this.disposed = true;
    this.channel?.removeEventListener("message", this.onChannelMessage);
    this.channel?.close();
    this.channel = null;
  }

  /** A new worker is waiting. An idle tab activates it; a busy tab waits. */
  onUpdateAvailable(): void {
    if (this.reloaded || this.gaveUp || this.disposed) return;
    this.pending = true;
    if (this.phase === "activating") return;
    this.tryApply();
  }

  /**
   * The waiting worker has activated, or this page's controller is already
   * behind `registration.active`. Safe to reload once the busy gate passes.
   */
  onWorkerActivated(): void {
    if (this.reloaded || this.gaveUp || this.disposed) return;
    this.pending = true;
    this.phase = "ready";
    this.tryApply();
  }

  /**
   * `controllerchange`. The first claim (this page had no controller) is not
   * an update. A later change means a new worker took this document, so the
   * page should reload once the busy gate passes. A change inside the reload
   * guard is remembered: tryApply holds it until the guard ends, and still
   * applies the busy check and the reload cap.
   */
  onControllerChange(hadController: boolean): void {
    if (this.reloaded || this.gaveUp || this.disposed) return;
    if (!hadController) return;
    if (!this.startedActivation) this.fromPeer = true;
    this.pending = true;
    this.phase = "ready";
    this.tryApply();
  }

  /** Busy state, visibility, or the retry timer may have moved. */
  recheck(): void {
    if (this.disposed) return;
    this.expireSignals();
    this.publishPresence();
    if (this.pending) this.tryApply();
  }

  private readonly onChannelMessage = (event: MessageEvent): void => {
    const msg = parseMessage(event.data);
    if (!msg || this.disposed) return;
    if (msg.type === "busy") {
      this.busySignals.set(msg.tabId, this.now());
      return;
    }
    if (msg.type === "idle") {
      this.busySignals.delete(msg.tabId);
      if (msg.tabId === this.tabId) this.tryApply();
      return;
    }
    if (msg.tabId === this.tabId) return;
    // The host confirms the worker is actually waiting or active. A page that
    // just loaded the new build hears this too, and must not reload again.
    this.fromPeer = true;
    this.opts.onExternalActivation?.();
  };

  private held(): boolean {
    this.expireSignals();
    return this.opts.isBusy() || this.busySignals.has(this.tabId);
  }

  private expireSignals(): void {
    const now = this.now();
    for (const [id, at] of this.busySignals) {
      if (now - at > BUSY_SIGNAL_TTL_MS) this.busySignals.delete(id);
    }
  }

  private publishPresence(): void {
    let busy = false;
    try {
      busy = this.opts.isBusy();
    } catch {
      busy = false;
    }
    if (busy === this.lastPublishedBusy) return;
    this.lastPublishedBusy = busy;
    this.post(busy ? { type: "busy", tabId: this.tabId } : { type: "idle", tabId: this.tabId });
  }

  private post(msg: PwaUpdateMessage): void {
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* channel closed or unavailable */
    }
  }

  private tryApply(): void {
    if (this.disposed || !this.pending || this.reloaded || this.gaveUp) return;
    if (this.held()) return;

    const record = readReloadRecord(this.storage, this.now());
    if (record.count >= MAX_RELOADS) {
      this.gaveUp = true;
      return;
    }
    if (record.at !== 0 && this.now() - record.at < RELOAD_GUARD_MS) return;

    if (this.phase === "idle") {
      this.phase = "activating";
      this.startedActivation = true;
      this.opts.skipWaiting();
      return;
    }
    if (this.phase !== "ready") return;

    this.reloaded = true;
    writeReloadRecord(this.storage, record, this.now());
    if (this.startedActivation && !this.fromPeer) {
      this.post({ type: "activated", tabId: this.tabId });
    }
    this.opts.reload();
  }
}
